import React, { useRef } from 'react';
import styles from './UserEmailInput.module.scss';
import { useOutsideClick } from '../../../utils/constants/hooks';
import { crossIconBlack } from '../../../assets/hostedassets';

interface User {
    emailId: string;
    userId?: string;
    isExternalUser?: boolean;
    [key: string]: any;
}

interface UserEmailInputProps {
    selectedUsers: User[];
    selectableUsers: User[];
    onAddUser: (email: string, isExternal?: boolean) => Promise<void> | void;
    onRemoveUser: (email: string) => void;
    // Props from useComposeEmailLogic
    inputValue: string;
    showDropdown: boolean;
    filteredUsers: string[];
    selectedPills: Set<string>;
    isValidatingEmail: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    isSelectingFromDropdown: React.MutableRefObject<boolean>;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    handleDropdownItemClick: (email: string) => void;
    handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleInputFocus: () => void;
    handlePillClick: (email: string, e: React.MouseEvent) => void;
    handleCloseDropdown: () => void;
    setSelectedPills: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const UserEmailInput: React.FC<UserEmailInputProps> = ({
    selectedUsers,
    selectableUsers,
    onAddUser,
    onRemoveUser,
    // Props from useComposeEmailLogic
    inputValue,
    showDropdown,
    filteredUsers,
    selectedPills,
    isValidatingEmail,
    inputRef,
    isSelectingFromDropdown,
    handleInputChange,
    handleKeyPress,
    handleDropdownItemClick,
    handleInputBlur,
    handleInputFocus,
    handlePillClick,
    handleCloseDropdown,
    setSelectedPills,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Handle clicking outside to close dropdown
    useOutsideClick(containerRef, handleCloseDropdown);

    return (
        <div ref={containerRef} className={styles.UserEmailInput}>
            <div className={styles.InputContainer}>
                <span className={styles.ToLabel}>To:</span>
                <div className={styles.PillsAndInput}>
                    {selectedUsers.map((user) => {
                        const isSelected = selectedPills.has(user.emailId);

                        return (
                            <div
                                key={user.emailId}
                                className={`${styles.UserPill} ${isSelected ? styles.SelectedPill : ''}`}
                                onClick={(e) => handlePillClick(user.emailId, e)}
                            >
                                <span className={styles.Email}>{user.emailId}</span>
                                <img
                                    className={`${styles.RemoveButton} ${selectedUsers.length <= 1 ? styles.DisabledRemoveButton : ''}`}
                                    src={crossIconBlack}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Only allow removal if there's more than 1 user
                                        if (selectedUsers.length <= 1) return;

                                        onRemoveUser(user.emailId);
                                        // Remove from selected pills if it was selected
                                        if (selectedPills.has(user.emailId)) {
                                            const newSelectedPills = new Set(selectedPills);
                                            newSelectedPills.delete(user.emailId);
                                            setSelectedPills(newSelectedPills);
                                        }
                                    }}
                                    alt="Remove"
                                    aria-label={`Remove ${user.emailId}`}
                                />
                            </div>
                        );
                    })}
                    <input
                        ref={inputRef}
                        type="email"
                        className={styles.EmailInput}
                        placeholder={selectedUsers.length === 0 ? 'Enter email address...' : ''}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        onBlur={handleInputBlur}
                        onFocus={handleInputFocus}
                        disabled={isValidatingEmail}
                    />
                    {isValidatingEmail && <span className={styles.ValidationText}>Creating user...</span>}
                </div>
            </div>

            {showDropdown && filteredUsers.length > 0 && (
                <div ref={dropdownRef} className={styles.Dropdown}>
                    {filteredUsers.map((email) => (
                        <div key={email} className={styles.DropdownItem} onClick={() => handleDropdownItemClick(email)}>
                            {email}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UserEmailInput;
